<template>
  <div class="tct-wrapper">
    <!-- Dashboard tab (multi-center only) -->
    <router-link :to="{ name: 'Dashboard' }" custom v-slot="{ navigate }">
      <button
        class="tct-tab"
        :class="{ 'tct-tab--active': isDashboardActive }"
        @click="navigate"
      >
        <Squares2X2Icon class="h-4 w-4 stroke-[2]" />
        <span>{{ t('navigation.dashboard') }}</span>
      </button>
    </router-link>

    <!-- Center tabs -->
    <router-link
      v-for="center in centers"
      :key="center.id"
      :to="{ name: 'Center', params: { centerId: center.id } }"
      custom
      v-slot="{ navigate }"
    >
      <button
        :ref="(el) => setCenterRef(el as HTMLElement | null, center.id)"
        class="tct-tab"
        :class="{ 'tct-tab--active': activeCenterId === center.id }"
        @click="navigate"
      >
        <span class="tct-dot" :style="{ background: getCenterColor(center.id) }" />
        <span>{{ center.preferences?.label || center.title }}</span>
      </button>
    </router-link>

    <!-- Sliding underline indicator -->
    <span class="tct-indicator" :style="indicatorStyle" />
  </div>
</template>

<script setup lang="ts">
defineOptions({ name: 'TopbarCenterTabs' })

import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, type CSSProperties } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Squares2X2Icon } from '@heroicons/vue/24/outline'
import { useUserManagerStore } from '../../stores/userManagerStore'
import { getCenterColorById } from '../../utils/CenterColors'

const route = useRoute()
const { t } = useI18n()
const userManagerStore = useUserManagerStore()

const centers = computed(() => userManagerStore.displayedCenters)

const activeCenterId = computed(() => {
  if (route.name !== 'Center') return null
  return Number(route.params.centerId)
})

const isDashboardActive = computed(() => route.path.startsWith('/dashboard'))

const getCenterColor = (centerId: number): string => {
  return getCenterColorById(centerId, userManagerStore.displayedCenters)
}

// ── Sliding indicator ──
const centerRefs = new Map<number, HTMLElement>()

const setCenterRef = (el: HTMLElement | null, centerId: number) => {
  if (el) centerRefs.set(centerId, el)
  else centerRefs.delete(centerId)
}

const indicatorStyle = ref<CSSProperties>({
  opacity: '0',
  transform: 'translateX(0)',
  width: '0px',
})

const updateIndicator = () => {
  // Find the active button element
  let activeEl: HTMLElement | undefined
  if (activeCenterId.value != null) {
    activeEl = centerRefs.get(activeCenterId.value)
  }
  // For dashboard, get the first child button
  if (isDashboardActive.value) {
    const wrapper = document.querySelector('.tct-wrapper')
    activeEl = wrapper?.querySelector('.tct-tab--active') as HTMLElement | undefined
  }

  const wrapper = document.querySelector('.tct-wrapper') as HTMLElement | null
  if (!wrapper || !activeEl) {
    indicatorStyle.value = { opacity: '0', transform: 'translateX(0)', width: '0px' }
    return
  }

  const wrapperRect = wrapper.getBoundingClientRect()
  const tabRect = activeEl.getBoundingClientRect()
  const left = tabRect.left - wrapperRect.left + 6
  const width = tabRect.width - 12

  indicatorStyle.value = {
    opacity: '1',
    transform: `translateX(${left}px)`,
    width: `${width}px`,
  }
}

watch([activeCenterId, isDashboardActive], async () => {
  await nextTick()
  updateIndicator()
})

let resizeObserver: ResizeObserver | null = null

onMounted(async () => {
  await nextTick()
  updateIndicator()

  const wrapper = document.querySelector('.tct-wrapper')
  if (wrapper) {
    resizeObserver = new ResizeObserver(() => updateIndicator())
    resizeObserver.observe(wrapper)
  }
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})
</script>

<style scoped>
.tct-wrapper {
  display: flex;
  align-items: stretch;
  gap: 4px;
  position: relative;
  min-width: 0;
}

.tct-tab {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.005em;
  white-space: nowrap;
  padding: 6px 12px 8px;
  border: none;
  background: transparent;
  color: var(--muted-foreground);
  cursor: pointer;
  transition: color 0.15s ease;
  flex-shrink: 0;
}

.tct-tab:hover:not(.tct-tab--active) {
  color: var(--foreground);
}

.tct-tab--active {
  color: var(--foreground);
  font-weight: 800;
}

:is(.dark *).tct-tab--active {
  color: white;
}

.tct-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Sliding underline indicator */
.tct-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  border-radius: 2px 2px 0 0;
  background: var(--apex-cyan);
  box-shadow: 0 1px 6px rgba(6, 200, 255, 0.4);
  transition: transform 0.3s var(--ease-standard),
              width 0.3s var(--ease-standard),
              opacity 0.2s ease;
  pointer-events: none;
}

:is(.dark *).tct-indicator {
  box-shadow: 0 1px 8px rgba(6, 200, 255, 0.35);
}
</style>
